import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Minimal SigV4 for the browser. The chat proxy is an IAM-authenticated Lambda function URL, so
 * every request has to be signed; hand-rolling it avoids an SDK dependency for one signature.
 *
 * Only what a JSON POST/GET to a function URL needs: no query signing, no chunked payloads.
 */

const enc = new TextEncoder();
const hex = buf => Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('');
const sha256 = async data => hex(await crypto.subtle.digest('SHA-256', enc.encode(data)));
async function hmac(key, data) {
  const k = await crypto.subtle.importKey('raw', key, {
    name: 'HMAC',
    hash: 'SHA-256'
  }, false, ['sign']);
  return crypto.subtle.sign('HMAC', k, enc.encode(data));
}
/** Returns the headers to send: the caller's own, plus Authorization and the x-amz-* set. */
/**
 * AWS's URI encoding, which is stricter than encodeURIComponent: it also percent-encodes
 * ! ' ( ) *. Leaving those raw canonicalises the request differently from the way AWS does it, so
 * a search for a name like O'Brien is signed correctly and still rejected as SignatureDoesNotMatch.
 */
const awsEncode = value => encodeURIComponent(value).replace(/[!'()*]/g, c => "%".concat(c.charCodeAt(0).toString(16).toUpperCase()));
export async function signRequest(_ref) {
  let method = _ref.method,
    url = _ref.url,
    headers = _ref.headers,
    _ref$body = _ref.body,
    body = _ref$body === void 0 ? '' : _ref$body,
    credentials = _ref.credentials,
    region = _ref.region,
    service = _ref.service,
    _ref$date = _ref.date,
    date = _ref$date === void 0 ? new Date() : _ref$date;
  const _URL = new URL(url),
    host = _URL.host,
    pathname = _URL.pathname,
    searchParams = _URL.searchParams;
  const amzDate = date.toISOString().replace(/[-:]|\.\d{3}/g, '');
  const shortDate = amzDate.slice(0, 8);
  const payloadHash = await sha256(body);
  const signed = _objectSpread(_objectSpread({}, headers), {}, {
    host,
    'x-amz-date': amzDate,
    'x-amz-content-sha256': payloadHash
  });
  if (credentials.sessionToken) signed['x-amz-security-token'] = credentials.sessionToken;
  const names = Object.keys(signed).map(n => n.toLowerCase()).sort();
  const lower = Object.fromEntries(Object.entries(signed).map(_ref2 => {
    let _ref3 = _slicedToArray(_ref2, 2),
      k = _ref3[0],
      v = _ref3[1];
    return [k.toLowerCase(), String(v).trim()];
  }));
  const canonicalHeaders = names.map(n => "".concat(n, ":").concat(lower[n], "\n")).join('');
  const signedHeaders = names.join(';');
  const query = [...searchParams.entries()].map(_ref4 => {
    let _ref5 = _slicedToArray(_ref4, 2),
      k = _ref5[0],
      v = _ref5[1];
    return [awsEncode(k), awsEncode(v)];
  })
  // By encoded key, then encoded value. SigV4 requires both: sorting on the key alone leaves
  // repeated parameters in arrival order, which the signer and AWS can order differently, and
  // the request is rejected for a signature mismatch that looks like a credentials problem.
  .sort((_ref6, _ref7) => {
    let _ref8 = _slicedToArray(_ref6, 2),
      ak = _ref8[0],
      av = _ref8[1];
    let _ref9 = _slicedToArray(_ref7, 2),
      bk = _ref9[0],
      bv = _ref9[1];
    return ak < bk ? -1 : ak > bk ? 1 : av < bv ? -1 : av > bv ? 1 : 0;
  }).map(_ref0 => {
    let _ref1 = _slicedToArray(_ref0, 2),
      k = _ref1[0],
      v = _ref1[1];
    return "".concat(k, "=").concat(v);
  }).join('&');
  const canonicalRequest = [method.toUpperCase(), pathname || '/', query, canonicalHeaders, signedHeaders, payloadHash].join('\n');
  const scope = "".concat(shortDate, "/").concat(region, "/").concat(service, "/aws4_request");
  const stringToSign = ['AWS4-HMAC-SHA256', amzDate, scope, await sha256(canonicalRequest)].join('\n');
  let key = enc.encode("AWS4".concat(credentials.secretAccessKey));
  for (const part of [shortDate, region, service, 'aws4_request']) key = await hmac(key, part);
  const signature = hex(await hmac(key, stringToSign));
  return _objectSpread(_objectSpread({}, signed), {}, {
    Authorization: "AWS4-HMAC-SHA256 Credential=".concat(credentials.accessKeyId, "/").concat(scope, ", ") + "SignedHeaders=".concat(signedHeaders, ", Signature=").concat(signature)
  });
}